import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FundamentalsService } from '@app/modules/fundamentals-modules/fundamentals.service';
import { CitiesModel, GenresModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { CitiesService } from '@app/modules/cities-module/cities.service';
import { InputValidators } from '@app/core/validators/input.validator';
import { URLLinkValidate, PhoneNumberValidate } from '@app/utils/validator';
import { SocialPlatform, socialPlatformFromApiValue } from '@app/modules/account-modules/performers.model';
import { SOCIAL_PLATFORMS } from '@app/modules/performers-modules/client/performer-form.constants';
import { VENUE_FACILITY_OPTION_KEYS } from '@app/shared/venue-customer-profile-fields/venue-customer-form.constants';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  AddIndividualCustomerProfileDto,
  AddIndividualCustomerProfileResult,
  AddVenueProfileDto,
  AddVenueProfileResult
} from '@app/modules/booking-modules/booking.model';
import { Observable } from 'rxjs';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { ProfileIndividualCustomerCreateDialogData } from './profile-individual-customer-create-dialog.model';

@UntilDestroy()
@Component({
  selector: 'app-profile-individual-customer-create-dialog',
  templateUrl: './profile-individual-customer-create-dialog.component.html',
  styleUrls: ['./profile-individual-customer-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileIndividualCustomerCreateDialogComponent extends DevicesClass implements OnInit {
  readonly DTypes = DeviceTypes;
  readonly SocialPlatforms = SOCIAL_PLATFORMS;
  readonly CustomerTypes = [
    { id: 'individual', label: 'Частное лицо' },
    { id: 'venue', label: 'Юрлицо' }
  ] as const;

  form: FormGroup;
  Cities: CitiesModel = [];
  Genres: GenresModel = [];
  formSubmitted = false;
  submitLoading = false;
  globalError: string | null = null;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private fundamentalsService: FundamentalsService,
    private citiesService: CitiesService,
    private inputValidators: InputValidators,
    private bookingService: BookingService,
    private notificationService: NotificationService
  ) {
    super(injector);
    const d = (this.config.data || {}) as ProfileIndividualCustomerCreateDialogData;
    this.form = this.fb.group({
      customerType: ['individual'],
      name: [(d.defaultDisplayName || '').trim(), [Validators.maxLength(200)]],
      cityId: [d.defaultCityId && d.defaultCityId > 0 ? d.defaultCityId : 0, [Validators.required, this.inputValidators.checkIdValue]],
      phone: [''],
      hasTelegram: [false],
      hasMax: [false],
      venueName: [''],
      inn: [''],
      format: [''],
      description: ['', Validators.maxLength(1000)],
      venueTypeTag: [''],
      preferredGenres: [[]],
      preferredPerformerTypes: [[]],
      readyToAcceptApplications: [false],
      addressFullAddress: [''],
      addressDescription: [''],
      maxAudienceCapacity: [null],
      stageAreaSquareMeters: [null],
      timeLimit: [''],
      socialLinks: this.fb.array([])
    });
    VENUE_FACILITY_OPTION_KEYS.forEach(key => {
      this.form.addControl(`facility_${key}_level`, this.fb.control(''));
      this.form.addControl(`facility_${key}_notes`, this.fb.control(''));
    });
  }

  get customerType(): 'individual' | 'venue' {
    return (this.form.get('customerType')?.value || 'individual') as 'individual' | 'venue';
  }

  ngOnInit(): void {
    this.loadCities();
    this.setDefaultCityByIp();
    this.setInitialSocialLinks();
    this.form.get('customerType')?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.applyCustomerTypePhoneValidation();
    });
    this.applyCustomerTypePhoneValidation();
    this.form.markAsPristine();
  }

  private applyCustomerTypePhoneValidation(): void {
    const phoneCtrl = this.form.get('phone');
    const hasTelegramCtrl = this.form.get('hasTelegram');
    const hasMaxCtrl = this.form.get('hasMax');
    if (!phoneCtrl || !hasTelegramCtrl || !hasMaxCtrl) {
      return;
    }
    if (this.customerType === 'venue') {
      phoneCtrl.setValidators([Validators.required]);
    } else {
      phoneCtrl.setValue('', { emitEvent: false });
      phoneCtrl.clearValidators();
      hasTelegramCtrl.setValue(false, { emitEvent: false });
      hasMaxCtrl.setValue(false, { emitEvent: false });
    }
    phoneCtrl.updateValueAndValidity({ emitEvent: false });
  }

  get socialLinksArray(): FormArray {
    return this.form.get('socialLinks') as FormArray;
  }

  private loadCities(): void {
    const deps = this.fundamentalsService.citiesDependencies;
    if (deps?.length) {
      this.Cities = [{ name: 'Выберите город', id: 0 }, ...deps];
    }
    const genres = this.fundamentalsService.genresDependencies;
    if (genres?.length) {
      this.Genres = genres;
    }
    this.cdr.markForCheck();
  }

  private setDefaultCityByIp(): void {
    const cityId = this.form.get('cityId')?.value;
    if (cityId && Number(cityId) > 0) {
      return;
    }
    this.citiesService.cityIdByIp().pipe(untilDestroyed(this)).subscribe(
      city => {
        if (city?.id) {
          const pristine = this.form.pristine;
          this.form.patchValue({ cityId: city.id });
          if (pristine) {
            this.form.markAsPristine();
          }
          this.cdr.markForCheck();
        }
      },
      () => {}
    );
  }

  private createSocialLinkGroup(
    platform: SocialPlatform = SocialPlatform.Instagram,
    url: string = ''
  ): FormGroup {
    return this.fb.group({
      platform: [platform],
      url: [url, (c: AbstractControl): ValidationErrors | null =>
        c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null]
    });
  }

  private setInitialSocialLinks(): void {
    const d = (this.config.data || {}) as ProfileIndividualCustomerCreateDialogData;
    const defaults = (d.defaultSocialLinks || [])
      .map(link => ({
        platform: socialPlatformFromApiValue(link?.platform) ?? SocialPlatform.Instagram,
        url: (link?.url || '').trim()
      }))
      .filter(link => link.url.length > 0);
    if (defaults.length) {
      defaults.forEach(link => this.socialLinksArray.push(this.createSocialLinkGroup(link.platform, link.url)));
    } else {
      this.socialLinksArray.push(this.createSocialLinkGroup());
    }
    this.cdr.markForCheck();
  }

  addSocialLink(): void {
    this.socialLinksArray.push(this.createSocialLinkGroup());
    this.cdr.markForCheck();
  }

  removeSocialLink(i: number): void {
    this.socialLinksArray.removeAt(i);
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  onMaskPhone($event: { inputType?: string }): void {
    const phone: string = this.form.get('phone')?.value || '';
    if (phone.split('').length === 1) {
      if ($event.inputType === 'deleteContentBackward') {
        this.form.get('phone')?.setValue(phone.split('').slice(1).join(''));
      } else {
        this.form.get('phone')?.setValue('7' + phone);
      }
    }
  }

  get phoneValid(): boolean {
    const phone: string = this.form.get('phone')?.value || '';
    return PhoneNumberValidate(phone);
  }

  get messengerClickEnabled(): boolean {
    const phone: string = this.form.get('phone')?.value || '';
    return phone !== '' && PhoneNumberValidate(phone);
  }

  onSetMessengerValue(key: 'hasTelegram' | 'hasMax'): void {
    if (!this.messengerClickEnabled) {
      return;
    }
    const ctrl = this.form.get(key);
    if (!ctrl) {
      return;
    }
    ctrl.setValue(!ctrl.value);
    this.cdr.markForCheck();
  }

  onGetMessengerStatus(key: 'hasTelegram' | 'hasMax'): boolean {
    return !!this.form.get(key)?.value;
  }

  private normalizePhoneDigits(): string {
    const raw = (this.form.get('phone')?.value || '') as string;
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 11 && digits[0] === '7') {
      return digits.slice(1);
    }
    return digits;
  }

  private getValidSocialLinks(): { platform: SocialPlatform; url: string }[] {
    return (this.socialLinksArray.controls as FormGroup[])
      .map(ctrl => ({
        platform: ctrl.get('platform')?.value as SocialPlatform,
        url: (ctrl.get('url')?.value || '').trim()
      }))
      .filter(l => !!l.url && URLLinkValidate(l.url));
  }

  onCancel(): void {
    this.ref.close(false);
  }

  submit(): void {
    if (this.submitLoading) {
      return;
    }
    this.formSubmitted = true;
    this.globalError = null;

    if (this.customerType === 'venue' && !this.phoneValid) {
      this.cdr.markForCheck();
      return;
    }

    const socialOk = this.getValidSocialLinks().length >= 1;
    if (!socialOk) {
      this.globalError = 'Добавьте хотя бы одну корректную ссылку на соцсеть.';
      this.cdr.markForCheck();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const v = this.form.getRawValue();
    const nameTrim = (v.name || '').toString().trim();
    const payloadBase = {
      cityId: Number(v.cityId) || 0,
      socialLinks: this.getValidSocialLinks()
    };

    this.submitLoading = true;
    this.cdr.markForCheck();

    const request$: Observable<AddVenueProfileResult | AddIndividualCustomerProfileResult> =
      this.customerType === 'venue'
        ? this.bookingService.addVenueProfile({
            socialLinks: payloadBase.socialLinks,
            phoneNumber: {
              number: this.normalizePhoneDigits(),
              hasTelegram: !!v.hasTelegram,
              hasMax: !!v.hasMax
            },
            venueName: (v.venueName || '').toString().trim() || null,
            inn: (v.inn || '').toString().replace(/\D/g, '') || null,
            format: (v.format || '').toString().trim() || null,
            description: (v.description || '').toString().trim() || null,
            venueTypeTag: (v.venueTypeTag || '').toString().trim() || null,
            preferredGenres: (v.preferredGenres || []).length ? v.preferredGenres : null,
            preferredPerformerTypes: (v.preferredPerformerTypes || []).length ? v.preferredPerformerTypes : null,
            readyToAcceptApplications: !!v.readyToAcceptApplications,
            address: (v.addressFullAddress || v.addressDescription)
              ? {
                  fullAddress: (v.addressFullAddress || '').toString().trim() || null,
                  description: (v.addressDescription || '').toString().trim() || null
                }
              : null,
            facilities: this.buildFacilitiesPayload(v)
          } as AddVenueProfileDto)
        : this.bookingService.addIndividualCustomerProfile({
            ...payloadBase,
            name: nameTrim || null
          } as AddIndividualCustomerProfileDto);

    request$.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.submitLoading = false;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Профиль заказчика',
          content: 'Профиль заказчика создан.',
          icons: 'pi pi-check'
        });
        this.ref.close(true);
        this.cdr.markForCheck();
      },
      error: err => {
        this.submitLoading = false;
        const msg =
          (err && (err.message || err.error?.message)) ||
          (typeof err === 'string' ? err : '') ||
          'Не удалось создать профиль заказчика.';
        this.globalError = msg;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: msg
        });
        this.cdr.markForCheck();
      }
    });
  }

  private buildFacilitiesPayload(v: any): AddVenueProfileDto['facilities'] {
    const facilities: Record<string, unknown> = {};
    VENUE_FACILITY_OPTION_KEYS.forEach(key => {
      const level = (v[`facility_${key}_level`] || '').toString().trim();
      const notes = (v[`facility_${key}_notes`] || '').toString().trim();
      if (level || notes) {
        facilities[key] = { level: level || null, notes: notes || null };
      }
    });
    if (v.maxAudienceCapacity != null && v.maxAudienceCapacity !== '') {
      facilities.maxAudienceCapacity = Number(v.maxAudienceCapacity);
    }
    if (v.stageAreaSquareMeters != null && v.stageAreaSquareMeters !== '') {
      facilities.stageAreaSquareMeters = Number(v.stageAreaSquareMeters);
    }
    if (v.timeLimit) {
      facilities.timeLimit = (v.timeLimit || '').toString().trim() || null;
    }
    return Object.keys(facilities).length ? (facilities as AddVenueProfileDto['facilities']) : null;
  }
}
